import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/layout/Layout";

// Public/prerendered routes are imported eagerly so hydrateRoot can
// match the SSR-generated HTML without triggering a Suspense fallback
// (which would discard the prerendered markup).
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Software from "./pages/Software";
import Products from "./pages/Products";
import Auctions from "./pages/Auctions";
import Login from "./pages/Login";
import Register from "./pages/Register";

const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const SeoLanding = lazy(() => import("./pages/SeoLanding"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const AuctionDetail = lazy(() => import("./pages/AuctionDetail"));
const Favorites = lazy(() => import("./pages/Favorites"));
const CustomerInvoices = lazy(() => import("./pages/customer/Invoices"));
const EpcisInbox = lazy(() => import("./pages/customer/EpcisInbox"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminCustomers = lazy(() => import("./pages/admin/Customers"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminAuctions = lazy(() => import("./pages/admin/Auctions"));
const AdminCustomerPricing = lazy(() => import("./pages/admin/CustomerPricing"));
const AdminNetSuite = lazy(() => import("./pages/admin/NetSuite"));
const AdminPricingDashboard = lazy(() => import("./pages/admin/PricingDashboard"));
const AdminBulkUpload = lazy(() => import("./pages/admin/BulkUpload"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminCarts = lazy(() => import("./pages/admin/Carts"));
const ExpirationDashboard = lazy(() => import("./pages/warehouse/Expiration"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const Account = lazy(() => import("./pages/Account"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

import { AuthProvider } from "./hooks/useAuth";
import { CartProvider } from "./hooks/useCart";
import { OrderOnBehalfProvider } from "./hooks/useOrderOnBehalf";
import { UnreadContactCountProvider } from "./hooks/useUnreadContactCount";
import ErrorBoundary from "./components/ErrorBoundary";

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <UnreadContactCountProvider>
      <OrderOnBehalfProvider>
      <CartProvider>
      <Toaster position="bottom-right" richColors />
      <Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/software" element={<Software />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/categories/:slug" element={<SeoLanding mode="category" />} />
          <Route path="/manufacturers/:slug" element={<SeoLanding mode="manufacturer" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/account" element={<Account />} />
          <Route path="/auctions" element={<Auctions />} />
          <Route path="/auctions/:id" element={<AuctionDetail />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/my-invoices" element={<CustomerInvoices />} />
          <Route path="/my-invoices/:id" element={<Navigate to="/my-invoices" replace />} />
          <Route path="/epcis" element={<EpcisInbox />} />

          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/auctions" element={<AdminAuctions />} />
          <Route path="/admin/customer-pricing" element={<AdminCustomerPricing />} />
          <Route path="/admin/netsuite" element={<AdminNetSuite />} />
          <Route path="/admin/pricing" element={<AdminPricingDashboard />} />
          <Route path="/admin/bulk-upload" element={<AdminBulkUpload />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/carts" element={<AdminCarts />} />

          <Route path="/warehouse/expiration" element={<ExpirationDashboard />} />
          <Route path="/warehouse" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/orders" element={<Navigate to="/admin/orders" replace />} />
          <Route path="/warehouse/inventory" element={<Navigate to="/admin/products" replace />} />
          <Route path="/warehouse/locations" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/cycle-counts" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/quarantine" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/recall-trace" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/receiving" element={<Navigate to="/admin" replace />} />

          <Route path="/accountant/*" element={<Navigate to="/admin" replace />} />
          <Route path="/purchasing" element={<Navigate to="/admin" replace />} />
          <Route path="/sales/*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
      </Suspense>
      </CartProvider>
      </OrderOnBehalfProvider>
      </UnreadContactCountProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
